/**
 * In-app confirmation dialog — replaces window.confirm so destructive flows
 * (unlink / remove / delete) look like part of the application instead of a
 * browser alert. Styling mirrors NewStripeConnectAccountModal's inline modal.
 *
 * `message` accepts arbitrary JSX so callers can render structured content
 * (numbered steps, row lists) instead of \n-joined strings.
 */
import React from "react";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Disables both buttons and shows a working state while onConfirm runs. */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  busy = false,
  onConfirm,
  onCancel,
}) => {
  if (!open) return null;
  return (
    <>
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={busy ? undefined : onCancel}
      />
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-24 pointer-events-none">
        <div
          className="pointer-events-auto w-full max-w-xl bg-white border border-gray-200 rounded-lg shadow-lg p-5"
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-base font-semibold mb-2">{title}</div>
          <div className="text-sm text-gray-700 max-h-[50vh] overflow-y-auto">
            {message}
          </div>
          <div className="mt-4 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="whitespace-nowrap rounded border border-gray-300 bg-white text-gray-700 text-sm font-medium px-4 py-2 hover:bg-gray-50 disabled:opacity-50"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className="whitespace-nowrap rounded bg-red-600 text-white text-sm font-medium px-4 py-2 hover:bg-red-700 disabled:opacity-50"
            >
              {busy ? "Working…" : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConfirmDialog;
